import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { Tooltip, OverlayTrigger, Modal, Button, Table } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";

import { SVGIcon } from "./Base";
import EmptyData from "./EmptyData";

import {
  getStatusDisplay,
  Humanize,
  toPesoAmount,
  decryptPaymentDetails,
  normalizeText,
} from "../../app/helpers";

const getStatusBadgeClass = (status = "") => {
  const s = String(status).toLowerCase();
  if (s.includes("completed")) return "badge-light-success text-success";
  if (s.includes("cancelled")) return "badge-light-danger text-danger";
  if (s.includes("refunded")) return "badge-light-warning text-warning";
  if (s === "ready") return "badge-light-primary text-primary";
  if (s === "out_for_delivery") return "badge-light-info text-info";
  if (s === "accepted") return "badge-light-secondary text-dark";
  if (s === "new") return "badge-light-dark text-dark";
  return "badge-light text-dark";
};

const getOrderTypeConfig = (orderType = "") => {
  const type = String(orderType || "")
    .toLowerCase()
    .trim();
  switch (type) {
    case "pickup":
      return {
        icon: "fas fa-shopping-bag",
        badgeClass: "badge-light-primary text-primary",
        iconColorClass: "text-primary",
        style: {},
        iconStyle: {},
      };
    case "third_party_pickup":
      return {
        icon: "fas fa-box",
        badgeClass: "badge-light-primary text-primary",
        iconColorClass: "text-primary",
        style: {},
        iconStyle: {},
      };
    case "delivery":
      return {
        icon: "fas fa-motorcycle",
        badgeClass: "badge-light-info text-info",
        iconColorClass: "text-info",
        style: {},
        iconStyle: {},
      };
    case "curbside_pickup":
    case "curbside":
      return {
        icon: "fas fa-car",
        badgeClass: "badge-light-warning text-warning",
        iconColorClass: "text-warning",
        style: {},
        iconStyle: {},
      };
    case "dine_in":
      return {
        icon: "fas fa-utensils",
        badgeClass: "badge-light-danger text-danger",
        iconColorClass: "text-danger",
        style: {},
        iconStyle: {},
      };
    case "meal_plan":
    case "mealplan":
      return {
        icon: "far fa-calendar-check",
        badgeClass: "badge-light-success text-success",
        iconColorClass: "text-success",
        style: {},
        iconStyle: {},
      };
    case "reservation":
      return {
        icon: "far fa-calendar-alt",
        badgeClass: "badge-light",
        iconColorClass: "",
        style: {
          backgroundColor: "rgba(137, 80, 252, 0.12)",
          color: "#8950fc",
          borderColor: "rgba(137, 80, 252, 0.25)",
        },
        iconStyle: { color: "#8950fc" },
      };
    case "digital":
      return {
        icon: "fas fa-qrcode",
        badgeClass: "badge-light-dark text-dark",
        iconColorClass: "text-dark",
        style: {},
        iconStyle: {},
      };
    default:
      return {
        icon: "fas fa-receipt",
        badgeClass: "badge-light-secondary text-dark",
        iconColorClass: "text-dark",
        style: {},
        iconStyle: {},
      };
  }
};

const formatTimestamp = (ts) => {
  if (!ts) return "-";
  const num = Number(ts);
  const m = !isNaN(num)
    ? num < 1e11
      ? moment.unix(num)
      : moment(num)
    : moment(ts);
  return m.isValid() ? m.format("MMM DD, YYYY hh:mm:ss A") : String(ts);
};

const getSortedTimestamps = (timestamps = {}) => {
  if (!timestamps || typeof timestamps !== "object") return [];
  return Object.entries(timestamps)
    .filter(([, val]) => val !== null && val !== undefined && val !== "")
    .map(([status, ts]) => {
      const num = Number(ts);
      const m = !isNaN(num)
        ? num < 1e11
          ? moment.unix(num)
          : moment(num)
        : moment(ts);
      return {
        status,
        timestamp: ts,
        timeMs: m.isValid() ? m.valueOf() : 0,
        formatted: formatTimestamp(ts),
        relative: m.isValid() ? m.fromNow() : "",
      };
    })
    .sort((a, b) => a.timeMs - b.timeMs);
};

const isOrderPreOrder = (order = {}) => {
  if (!order || typeof order !== "object") return false;
  if (order.is_asap === true || order.is_asap === "true") return false;
  const time = String(order.order_time || "")
    .trim()
    .toLowerCase();
  if (!time || time === "asap_order_time" || time === "asap") return false;
  return true;
};

const COLUMN_DEFINITIONS = [
  { id: "transaction_number", label: "Order #", width: 160 },
  { id: "order_date", label: "Order Date", width: 160 },
  { id: "store_name", label: "Store", width: 180 },
  { id: "order_type", label: "Order Type", width: 160 },
  { id: "pre_order", label: "Pre-Order", width: 130 },
  { id: "delivery_partner", label: "Delivery Partner", width: 140 },
  { id: "payment_details", label: "Payment ID", width: 160 },
  { id: "payment_method", label: "Payment Method", width: 170 },
  { id: "email", label: "Customer Email", width: 240 },
  { id: "customer_name", label: "Customer Name", width: 180 },
  { id: "contact_number", label: "Contact #", width: 150 },
  { id: "delivery_address", label: "Delivery Address", width: 320 },
  { id: "chargedDeliveryFee", label: "Delivery Charge", width: 160 },
  { id: "remarks", label: "Remarks", width: 190 },
  { id: "status", label: "Status", width: 160 },
  { id: "amount", label: "Total Amount", width: 160 },
  { id: "discounts", label: "Discounts", width: 160 },
  { id: "activity", label: "Activity", width: 140 },
  { id: "items", label: "Items (Breakdown)", width: 350 },
];

const STORAGE_KEY = "reports_table_column_visibility_v2";

const getDefaultColumnVisibility = () => {
  const initial = {};
  COLUMN_DEFINITIONS.forEach((col) => {
    initial[col.id] = true;
  });
  return initial;
};

const getInitialColumnVisibility = () => {
  if (typeof window === "undefined") {
    return getDefaultColumnVisibility();
  }
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const defaults = getDefaultColumnVisibility();
      let hasAnyTrue = false;
      Object.keys(defaults).forEach((key) => {
        if (typeof parsed[key] === "boolean") {
          defaults[key] = parsed[key];
        }
        if (defaults[key]) {
          hasAnyTrue = true;
        }
      });
      if (hasAnyTrue) {
        return defaults;
      }
    }
  } catch {
    // fallback
  }
  return getDefaultColumnVisibility();
};

const ReportsTable = (props) => {
  const { data = [], onClickSort, levelId = "", webview } = props;
  const { pathname } = useLocation();
  const [selectedTimelineOrder, setSelectedTimelineOrder] = useState(null);
  const [isAllItemsExpanded, setIsAllItemsExpanded] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(
    getInitialColumnVisibility,
  );
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [columnSearchTerm, setColumnSearchTerm] = useState("");
  const settingsRef = useRef(null);
  const [visibleWidth, setVisibleWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth - 60 : 1200,
  );
  const containerRef = useRef(null);

  const preOrderCount = Array.isArray(data)
    ? data.filter(isOrderPreOrder).length
    : 0;

  const visibleColumnCount = COLUMN_DEFINITIONS.filter(
    (col) => visibleColumns[col.id],
  ).length;

  const totalTableWidth = COLUMN_DEFINITIONS.filter(
    (col) => visibleColumns[col.id],
  ).reduce((sum, col) => sum + (col.width || 160), 0);

  const filteredColumns = COLUMN_DEFINITIONS.filter((col) =>
    col.label.toLowerCase().includes(columnSearchTerm.trim().toLowerCase()),
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setIsSettingsOpen(false);
      }
    };
    if (isSettingsOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSettingsOpen]);

  const handleToggleColumn = (colId) => {
    setVisibleColumns((prev) => {
      const next = { ...prev, [colId]: !prev[colId] };
      if (!Object.values(next).some(Boolean)) {
        return prev;
      }
      try {
        if (typeof window !== "undefined") {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        }
      } catch {
        // ignore
      }
      return next;
    });
  };

  const handleShowAllColumns = () => {
    const allVisible = getDefaultColumnVisibility();
    setVisibleColumns(allVisible);
    try {
      if (typeof window !== "undefined") {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(allVisible));
      }
    } catch {
      // ignore
    }
  };

  const handleResetColumns = () => {
    const defaultVisible = getDefaultColumnVisibility();
    setVisibleColumns(defaultVisible);
    try {
      if (typeof window !== "undefined") {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultVisible));
      }
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    // Unconstrain parent .container so the table is full width across the viewport
    const parentContainer =
      containerRef.current?.closest(".container") ||
      document.querySelector("#kt_content .container");
    if (parentContainer) {
      parentContainer.classList.add("reports-container-fluid");
      parentContainer.style.maxWidth = "100%";
      parentContainer.style.width = "100%";
      parentContainer.style.paddingLeft = "25px";
      parentContainer.style.paddingRight = "25px";
    }

    const updateWidth = () => {
      if (containerRef.current) {
        setVisibleWidth(containerRef.current.clientWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);

    let ro;
    if (typeof ResizeObserver !== "undefined" && containerRef.current) {
      ro = new ResizeObserver(() => {
        updateWidth();
      });
      ro.observe(containerRef.current);
    }

    return () => {
      if (parentContainer) {
        parentContainer.classList.remove("reports-container-fluid");
        parentContainer.style.maxWidth = "";
        parentContainer.style.width = "";
        parentContainer.style.paddingLeft = "";
        parentContainer.style.paddingRight = "";
      }
      window.removeEventListener("resize", updateWidth);
      if (ro) ro.disconnect();
    };
  }, []);

  const renderSortButton = (field, label) => (
    <button
      type="button"
      className="btn btn-icon btn-xs btn-clean ml-1"
      title={`Sort by ${label}`}
      onClick={() => onClickSort && onClickSort(field)}
    >
      <SVGIcon icon={"Shopping/Sort1"} />
    </button>
  );

  return (
    <>
      {data.length === 0 || !data ? (
        <EmptyData className={"py-8 px-6 text-center bg-white"} />
      ) : (
        <>
          {/* Table Settings and Summary Toolbar */}
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
            <div className="d-flex align-items-center flex-wrap mr-2 my-1">
              <span className="text-muted font-size-sm">
                Showing <strong className="text-dark">{data.length}</strong>{" "}
                order{data.length !== 1 ? "s" : ""}
              </span>
              {preOrderCount > 0 && (
                <span
                  className="badge font-weight-bolder ml-3 py-1 px-2 font-size-xs d-inline-flex align-items-center"
                  style={{
                    backgroundColor: "rgba(137, 80, 252, 0.12)",
                    color: "#8950fc",
                    border: "1px solid rgba(137, 80, 252, 0.25)",
                  }}
                  title={`${preOrderCount} scheduled pre-orders in this view`}
                >
                  <i
                    className="far fa-calendar-alt mr-1 font-size-xs"
                    style={{ fontSize: "9px", color: "#8950fc" }}
                  />
                  {preOrderCount} Pre-Order{preOrderCount !== 1 ? "s" : ""}
                </span>
              )}
              {visibleColumnCount < COLUMN_DEFINITIONS.length && (
                <span
                  className="badge font-weight-bolder ml-3 py-1 px-2 font-size-xs d-inline-flex align-items-center"
                  style={{
                    backgroundColor: "#f3f6f9",
                    color: "#4b5563",
                    border: "1px solid #d1d5db",
                  }}
                >
                  <i className="fas fa-eye-slash mr-1 font-size-xs text-muted" />
                  {COLUMN_DEFINITIONS.length - visibleColumnCount} column
                  {COLUMN_DEFINITIONS.length - visibleColumnCount !== 1
                    ? "s"
                    : ""}{" "}
                  hidden
                  <button
                    type="button"
                    className="btn btn-xs btn-link p-0 ml-2 font-weight-bold"
                    style={{ color: "#4b5563", textDecoration: "underline" }}
                    onClick={handleShowAllColumns}
                  >
                    Show all
                  </button>
                </span>
              )}
            </div>

            <div className="d-flex align-items-center my-1 ml-auto">
              <div
                ref={settingsRef}
                className={`dropdown ${isSettingsOpen ? "show" : ""}`}
                style={{ position: "relative" }}
              >
                <button
                  type="button"
                  id="reports-table-settings-toggle"
                  className="btn btn-sm font-weight-bold py-2 px-3 d-inline-flex align-items-center shadow-xs"
                  style={{
                    backgroundColor: "#f3f6f9",
                    borderColor: "#d1d5db",
                    color: "#4b5563",
                    border: "1px solid #d1d5db",
                    fontSize: "12px",
                  }}
                  onClick={() => setIsSettingsOpen(!isSettingsOpen)}
                  title="Configure Table Columns"
                >
                  <i className="fas fa-sliders-h mr-2 text-muted" />
                  <span>Table Settings</span>
                  <span
                    className="badge ml-2 font-weight-bolder py-0 px-2 font-size-xs"
                    style={{
                      backgroundColor: "#e4e6ef",
                      color: "#3f4254",
                    }}
                  >
                    {visibleColumnCount}/{COLUMN_DEFINITIONS.length}
                  </span>
                </button>

                {isSettingsOpen && (
                  <div
                    className="dropdown-menu dropdown-menu-right p-3 shadow-lg border-0 rounded-lg show reports-table-settings-menu"
                    style={{
                      width: "320px",
                      minWidth: "280px",
                      maxWidth: "min(340px, calc(100vw - 32px))",
                      position: "absolute",
                      right: 0,
                      left: "auto",
                      marginLeft: 0,
                      marginRight: 0,
                      top: "100%",
                      marginTop: "6px",
                      zIndex: 1050,
                      backgroundColor: "#ffffff",
                      border: "1px solid #e5e7eb",
                      boxShadow:
                        "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="d-flex justify-content-between align-items-center pb-2 mb-2 border-bottom">
                      <div>
                        <h6 className="font-weight-bolder text-dark mb-0 font-size-sm">
                          <i className="fas fa-sliders-h mr-2 text-muted" />
                          Table Settings
                        </h6>
                        <span className="text-muted font-size-xs">
                          Show or hide columns
                        </span>
                      </div>
                      <div className="d-flex align-items-center">
                        <button
                          type="button"
                          className="btn btn-xs btn-link font-weight-bold p-0 mr-2"
                          style={{ color: "#4b5563" }}
                          onClick={handleShowAllColumns}
                          title="Show all columns"
                        >
                          Show all
                        </button>
                        <span className="text-muted font-size-xs mr-2">•</span>
                        <button
                          type="button"
                          className="btn btn-xs btn-link text-muted font-weight-bold p-0"
                          onClick={handleResetColumns}
                          title="Reset to default columns"
                        >
                          Reset
                        </button>
                      </div>
                    </div>

                    {/* Column Search Filter */}
                    <div className="mb-2">
                      <div className="input-icon input-icon-right">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Search columns..."
                          value={columnSearchTerm}
                          onChange={(e) => setColumnSearchTerm(e.target.value)}
                          style={{
                            fontSize: "12px",
                            height: "32px",
                            backgroundColor: "#f3f6f9",
                            borderColor: "#d1d5db",
                            color: "#374151",
                          }}
                        />
                        <span>
                          <i className="fas fa-search text-muted font-size-xs" />
                        </span>
                      </div>
                    </div>

                    {/* Columns Checklist */}
                    <div
                      className="pr-1"
                      style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        overflowX: "hidden",
                      }}
                    >
                      {filteredColumns.map((col) => {
                        const isChecked = Boolean(visibleColumns[col.id]);
                        const isOnlyOneVisible =
                          isChecked && visibleColumnCount === 1;

                        return (
                          <div
                            key={col.id}
                            className="d-flex align-items-center justify-content-between py-1 px-2 rounded mb-1"
                            style={{
                              cursor: isOnlyOneVisible
                                ? "not-allowed"
                                : "pointer",
                              transition: "background-color 0.12s ease",
                              backgroundColor: isChecked
                                ? "#f3f6f9"
                                : "transparent",
                              border: isChecked
                                ? "1px solid #e5e7eb"
                                : "1px solid transparent",
                            }}
                            onMouseEnter={(e) => {
                              if (!isOnlyOneVisible) {
                                e.currentTarget.style.backgroundColor =
                                  isChecked ? "#e4e6ef" : "#f9fafb";
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = isChecked
                                ? "#f3f6f9"
                                : "transparent";
                            }}
                            onClick={() => {
                              if (!isOnlyOneVisible) {
                                handleToggleColumn(col.id);
                              }
                            }}
                          >
                            <div className="d-flex align-items-center flex-grow-1 text-truncate mr-2">
                              <input
                                type="checkbox"
                                className="mr-2 cursor-pointer"
                                checked={isChecked}
                                disabled={isOnlyOneVisible}
                                readOnly
                                style={{
                                  cursor: isOnlyOneVisible
                                    ? "not-allowed"
                                    : "pointer",
                                  pointerEvents: "none",
                                  accentColor: "#6b7280",
                                }}
                              />
                              <span
                                className={`text-truncate font-size-xs ${
                                  isChecked
                                    ? "font-weight-bold text-dark"
                                    : "text-muted"
                                }`}
                              >
                                {col.label}
                              </span>
                            </div>
                            <span
                              className="badge font-size-xs px-2 py-0"
                              style={{
                                backgroundColor: isChecked
                                  ? "#e4e6ef"
                                  : "#f3f6f9",
                                color: "#6b7280",
                              }}
                            >
                              {col.width}px
                            </span>
                          </div>
                        );
                      })}
                      {filteredColumns.length === 0 && (
                        <div className="text-center py-4 text-muted font-size-xs">
                          No columns match &quot;{columnSearchTerm}&quot;
                        </div>
                      )}
                    </div>

                    <div className="d-flex justify-content-between align-items-center pt-2 mt-2 border-top">
                      <span className="text-muted font-size-xs">
                        {visibleColumnCount} of {COLUMN_DEFINITIONS.length}{" "}
                        visible
                      </span>
                      <button
                        type="button"
                        className="btn btn-xs font-weight-bold py-1 px-3"
                        style={{
                          backgroundColor: "#f3f6f9",
                          borderColor: "#d1d5db",
                          color: "#4b5563",
                          border: "1px solid #d1d5db",
                        }}
                        onClick={() => setIsSettingsOpen(false)}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            ref={containerRef}
            className="table-responsive bg-white rounded shadow-xs border reports-table-container w-100"
            style={{ width: "100%", overflowX: "auto" }}
          >
            <table
              className="table table-head-custom table-vertical-center table-hover mb-0"
              style={{ minWidth: `${totalTableWidth}px`, width: "100%" }}
            >
              <thead className="thead-light">
                <tr className="text-uppercase text-secondary font-weight-bolder font-size-xs border-bottom">
                  {visibleColumns.transaction_number && (
                    <th
                      scope="col"
                      className="align-middle text-nowrap py-4 px-4"
                      style={{ minWidth: "160px", width: "160px" }}
                    >
                      <div className="d-inline-flex align-items-center">
                        <span>ORDER#</span>
                        {renderSortButton("transaction_number", "Order Number")}
                      </div>
                    </th>
                  )}
                  {visibleColumns.order_date && (
                    <th
                      scope="col"
                      className="align-middle text-nowrap py-4 px-4"
                      style={{ minWidth: "160px", width: "160px" }}
                    >
                      <div className="d-inline-flex align-items-center">
                        <span>ORDER DATE</span>
                        {renderSortButton("order_date", "Order Date")}
                      </div>
                    </th>
                  )}
                  {visibleColumns.store_name && (
                    <th
                      scope="col"
                      className="align-middle text-nowrap py-4 px-4"
                      style={{ minWidth: "180px", width: "180px" }}
                    >
                      STORE
                    </th>
                  )}
                  {visibleColumns.order_type && (
                    <th
                      scope="col"
                      className="align-middle text-nowrap py-4 px-4"
                      style={{ minWidth: "160px", width: "160px" }}
                    >
                      <div className="d-inline-flex align-items-center">
                        <span>ORDER TYPE</span>
                        {renderSortButton("order_type", "Order Type")}
                      </div>
                    </th>
                  )}
                  {visibleColumns.pre_order && (
                    <th
                      scope="col"
                      className="align-middle text-nowrap py-4 px-4"
                      style={{ minWidth: "130px", width: "130px" }}
                    >
                      <div className="d-inline-flex align-items-center">
                        <span>PRE-ORDER</span>
                        {renderSortButton("is_asap", "Pre-Order")}
                      </div>
                    </th>
                  )}
                  {visibleColumns.delivery_partner && (
                    <th
                      scope="col"
                      className="align-middle text-nowrap py-4 px-4"
                      style={{ minWidth: "140px", width: "140px" }}
                    >
                      DELIVERY
                    </th>
                  )}
                  {visibleColumns.payment_details && (
                    <th
                      scope="col"
                      className="align-middle text-nowrap py-4 px-4"
                      style={{ minWidth: "160px", width: "160px" }}
                    >
                      PAYMENT ID
                    </th>
                  )}
                  {visibleColumns.payment_method && (
                    <th
                      scope="col"
                      className="align-middle text-nowrap py-4 px-4"
                      style={{ minWidth: "170px", width: "170px" }}
                    >
                      <div className="d-inline-flex align-items-center">
                        <span>PAYMENT METHOD</span>
                        {renderSortButton("payment_method", "Payment Method")}
                      </div>
                    </th>
                  )}
                  {visibleColumns.email && (
                    <th
                      scope="col"
                      className="align-middle text-nowrap py-4 px-4"
                      style={{ minWidth: "240px", width: "240px" }}
                    >
                      <div className="d-inline-flex align-items-center">
                        <span>CUSTOMER EMAIL</span>
                        {renderSortButton("email", "Customer Email")}
                      </div>
                    </th>
                  )}
                  {visibleColumns.customer_name && (
                    <th
                      scope="col"
                      className="align-middle text-nowrap py-4 px-4"
                      style={{ minWidth: "180px", width: "180px" }}
                    >
                      <div className="d-inline-flex align-items-center">
                        <span>CUSTOMER NAME</span>
                        {renderSortButton("customer_name", "Customer Name")}
                      </div>
                    </th>
                  )}
                  {visibleColumns.contact_number && (
                    <th
                      scope="col"
                      className="align-middle text-nowrap py-4 px-4"
                      style={{ minWidth: "150px", width: "150px" }}
                    >
                      <div className="d-inline-flex align-items-center">
                        <span>CONTACT #</span>
                        {renderSortButton("contact_number", "Contact Number")}
                      </div>
                    </th>
                  )}
                  {visibleColumns.delivery_address && (
                    <th
                      scope="col"
                      className="align-middle text-nowrap py-4 px-4"
                      style={{ minWidth: "300px", width: "320px" }}
                    >
                      <div className="d-inline-flex align-items-center">
                        <span>DELIVERY ADDRESS</span>
                        {renderSortButton(
                          "delivery_address",
                          "Delivery Address",
                        )}
                      </div>
                    </th>
                  )}
                  {visibleColumns.chargedDeliveryFee && (
                    <th
                      scope="col"
                      className="align-middle text-nowrap py-4 px-4"
                      style={{ minWidth: "160px", width: "160px" }}
                    >
                      <div className="d-inline-flex align-items-center">
                        <span>DELIVERY CHARGE</span>
                        {renderSortButton(
                          "chargedDeliveryFee",
                          "Delivery Charge",
                        )}
                      </div>
                    </th>
                  )}
                  {visibleColumns.remarks && (
                    <th
                      scope="col"
                      className="align-middle text-nowrap py-4 px-4"
                      style={{ minWidth: "190px", width: "190px" }}
                    >
                      REMARKS
                    </th>
                  )}
                  {visibleColumns.status && (
                    <th
                      scope="col"
                      className="align-middle text-nowrap py-4 px-4"
                      style={{ minWidth: "160px", width: "160px" }}
                    >
                      <div className="d-inline-flex align-items-center">
                        <span>STATUS</span>
                        {renderSortButton("status", "Status")}
                      </div>
                    </th>
                  )}
                  {visibleColumns.amount && (
                    <th
                      scope="col"
                      className="align-middle text-nowrap py-4 px-4"
                      style={{ minWidth: "160px", width: "160px" }}
                    >
                      <div className="d-inline-flex align-items-center">
                        <span>TOTAL AMOUNT</span>
                        {renderSortButton("amount", "Total Amount")}
                      </div>
                    </th>
                  )}
                  {visibleColumns.discounts && (
                    <th
                      scope="col"
                      className="align-middle text-nowrap py-4 px-4"
                      style={{ minWidth: "160px", width: "160px" }}
                    >
                      DISCOUNTS
                    </th>
                  )}
                  {visibleColumns.activity && (
                    <th
                      scope="col"
                      className="align-middle text-center text-nowrap py-4 px-4"
                      style={{ minWidth: "140px", width: "140px" }}
                    >
                      ACTIVITY
                    </th>
                  )}
                  {visibleColumns.items && (
                    <th
                      scope="col"
                      className="align-middle text-nowrap py-4 px-4"
                      style={{ minWidth: "330px", width: "350px" }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <span className="font-weight-bolder">
                          ITEMS (BREAKDOWN)
                        </span>
                        <button
                          type="button"
                          className="btn btn-xs font-weight-bold py-1 px-3 d-inline-flex align-items-center"
                          style={{
                            fontSize: "11px",
                            letterSpacing: "0.2px",
                            backgroundColor: isAllItemsExpanded
                              ? "#e4e6ef"
                              : "#f3f6f9",
                            borderColor: "#d1d5db",
                            color: "#4b5563",
                            border: "1px solid #d1d5db",
                          }}
                          title={
                            isAllItemsExpanded
                              ? "Collapse items breakdown for all rows"
                              : "Expand items breakdown for all rows"
                          }
                          onClick={() =>
                            setIsAllItemsExpanded(!isAllItemsExpanded)
                          }
                        >
                          <i
                            className={`fas fa-${
                              isAllItemsExpanded ? "compress-alt" : "expand-alt"
                            } mr-1 font-size-xs`}
                          />
                          <span>
                            {isAllItemsExpanded
                              ? "Collapse Breakdown"
                              : "Expand Breakdown"}
                          </span>
                        </button>
                      </div>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.map((order, i) => (
                  <TableRow
                    key={(order._id || "") + i}
                    order={order}
                    webview={webview}
                    levelId={levelId}
                    visibleWidth={visibleWidth}
                    isItemsExpanded={isAllItemsExpanded}
                    visibleColumns={visibleColumns}
                    visibleColumnCount={visibleColumnCount}
                    onSelectTimeline={() => setSelectedTimelineOrder(order)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Activity Timeline Modal */}
          <Modal
            show={Boolean(selectedTimelineOrder)}
            onHide={() => setSelectedTimelineOrder(null)}
            centered
            size="lg"
          >
            <Modal.Header closeButton className="border-bottom">
              <Modal.Title className="font-weight-bolder text-dark">
                Order #{selectedTimelineOrder?.transaction_number} - Status
                Activity
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-6">
              {selectedTimelineOrder && (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-5 p-3 bg-light rounded">
                    <div>
                      <div className="font-weight-bold text-dark d-flex align-items-center">
                        <span>
                          {selectedTimelineOrder.store_name || "Store"}
                        </span>
                        {selectedTimelineOrder.order_type &&
                          (() => {
                            const cfg = getOrderTypeConfig(
                              selectedTimelineOrder.order_type,
                            );
                            return (
                              <span
                                className={`badge ml-2 font-weight-bolder text-capitalize py-1 px-2 font-size-xs d-inline-flex align-items-center ${cfg.badgeClass}`}
                                style={cfg.style}
                              >
                                <i
                                  className={`${cfg.icon} mr-1 font-size-xs ${cfg.iconColorClass}`}
                                  style={cfg.iconStyle}
                                />
                                {Humanize(selectedTimelineOrder.order_type)}
                              </span>
                            );
                          })()}
                        {isOrderPreOrder(selectedTimelineOrder) && (
                          <span
                            className="badge font-weight-bolder text-uppercase py-1 px-2 font-size-xs ml-2 d-inline-flex align-items-center"
                            style={{
                              backgroundColor: "rgba(137, 80, 252, 0.12)",
                              color: "#8950fc",
                              border: "1px solid rgba(137, 80, 252, 0.25)",
                              borderRadius: "4px",
                            }}
                          >
                            <i
                              className="far fa-calendar-alt mr-1"
                              style={{ fontSize: "9px", color: "#8950fc" }}
                            />
                            Pre-Order
                          </span>
                        )}
                      </div>
                      <div className="text-muted font-size-xs">
                        Customer:{" "}
                        {selectedTimelineOrder.customer_name || "Guest"}
                        {selectedTimelineOrder.delivery_partner && (
                          <span className="ml-2 font-weight-bold text-primary text-capitalize">
                            • {Humanize(selectedTimelineOrder.delivery_partner)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <span
                        className={`badge font-weight-bolder text-uppercase py-2 px-3 ${getStatusBadgeClass(
                          selectedTimelineOrder.status,
                        )}`}
                      >
                        {getStatusDisplay(
                          selectedTimelineOrder.status,
                          selectedTimelineOrder.order_type,
                        )}
                      </span>
                    </div>
                  </div>

                  {(() => {
                    let parsedTs = {};
                    if (selectedTimelineOrder.change_timestamp) {
                      try {
                        parsedTs =
                          typeof selectedTimelineOrder.change_timestamp ===
                          "string"
                            ? JSON.parse(selectedTimelineOrder.change_timestamp)
                            : selectedTimelineOrder.change_timestamp;
                      } catch {
                        parsedTs = {};
                      }
                    }
                    const sorted = getSortedTimestamps(parsedTs);

                    if (sorted.length === 0) {
                      return (
                        <div className="text-center py-6 text-muted">
                          <i className="far fa-clock fa-2x mb-2 d-block text-secondary" />
                          No status transition timestamps recorded for this
                          order.
                        </div>
                      );
                    }

                    return (
                      <div className="timeline timeline-3">
                        <div className="timeline-items">
                          {sorted.map((step, idx, arr) => {
                            const prevStep = idx > 0 ? arr[idx - 1] : null;
                            const diffMins =
                              prevStep && step.timeMs && prevStep.timeMs
                                ? Math.round(
                                    (step.timeMs - prevStep.timeMs) / 60000,
                                  )
                                : null;

                            return (
                              <div
                                key={step.status}
                                className="d-flex align-items-start mb-4 pb-3 border-bottom border-light"
                              >
                                <div
                                  className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mr-3 font-weight-bold font-size-xs flex-shrink-0"
                                  style={{ width: "28px", height: "28px" }}
                                >
                                  {idx + 1}
                                </div>
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center justify-content-between">
                                    <span
                                      className={`badge font-weight-bold ${getStatusBadgeClass(
                                        step.status,
                                      )}`}
                                    >
                                      {getStatusDisplay(step.status)}
                                    </span>
                                    {diffMins !== null && diffMins >= 0 && (
                                      <span className="badge badge-light-secondary font-size-xs text-muted">
                                        +{diffMins} min
                                        {diffMins !== 1 ? "s" : ""}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-dark font-weight-bold font-size-sm mt-1">
                                    {step.formatted}
                                  </div>
                                  {step.relative && (
                                    <div className="text-muted font-size-xs">
                                      {step.relative}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </Modal.Body>
            <Modal.Footer className="border-top d-flex justify-content-between align-items-center">
              {selectedTimelineOrder &&
              !webview &&
              selectedTimelineOrder._id ? (
                <Link
                  to={{
                    pathname: `/orders/${selectedTimelineOrder._id}`,
                    state: { from: pathname },
                  }}
                  className="btn btn-primary btn-sm font-weight-bold rounded-pill py-2 px-4 shadow-xs text-white d-inline-flex align-items-center"
                  title="Open full order in console"
                >
                  <i className="fas fa-external-link-alt mr-2 font-size-xs" />
                  View Actual Order
                </Link>
              ) : (
                <div />
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedTimelineOrder(null)}
              >
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </>
  );
};

function TableRow({
  order,
  webview = "",
  visibleWidth,
  isItemsExpanded = false,
  visibleColumns = {},
  visibleColumnCount = 18,
  onSelectTimeline,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { pathname } = useLocation();

  const {
    order_date,
    order_time,
    _id,
    transaction_number,
    order_type,
    email,
    customer_name,
    contact_number,
    delivery_address,
    chargedDeliveryFee,
    status,
    returning_customer,
    payment_details = "",
    payment_method = "",
    order_details = [],
    store_name = "",
    delivery_partner = "",
    customer_remarks = "",
    notes = "",
    promo_discount = 0,
    promoDiscount = 0,
    change_timestamp = "",
  } = order;

  const remarks = customer_remarks || notes || "";
  const promo = promo_discount || promoDiscount || 0;
  const isPreOrder = isOrderPreOrder(order);

  // Calculate item discounts
  let totalItemDiscount = 0;
  order_details.forEach((detail) => {
    const unitPrice =
      detail.price !== undefined && detail.price !== null
        ? Number(detail.price)
        : 0;
    const regularPrice =
      detail.regular_price !== undefined && detail.regular_price !== null
        ? Number(detail.regular_price)
        : unitPrice;
    const qty = Number(detail.qty) || 1;
    if (regularPrice > unitPrice) {
      totalItemDiscount += (regularPrice - unitPrice) * qty;
    }
  });

  // Calculate count of recorded timestamp transitions
  let parsedTs = {};
  if (change_timestamp) {
    try {
      parsedTs =
        typeof change_timestamp === "string"
          ? JSON.parse(change_timestamp)
          : change_timestamp;
    } catch {
      parsedTs = {};
    }
  }
  const timestampCount = Object.keys(parsedTs || {}).length;

  const totalAmountDisplay = (orderObj = {}) => {
    const { isMealPlan = false, amount = 0, refunded_amount = 0 } = orderObj;

    if (isMealPlan) {
      return (
        <OverlayTrigger
          placement="right"
          delay={{ show: 250, hide: 400 }}
          overlay={(props) => (
            <Tooltip id="button-tooltip-meal-plan" {...props}>
              Meal Plan
            </Tooltip>
          )}
        >
          <span>
            {toPesoAmount(amount - refunded_amount)}
            <i className="far fa-calendar-check ml-2 text-success" />
          </span>
        </OverlayTrigger>
      );
    }

    return toPesoAmount(amount);
  };

  return (
    <>
      <tr
        className="border-bottom"
        style={{
          cursor: "pointer",
          backgroundColor: isExpanded ? "#f4f6fc" : "inherit",
          transition: "background-color 0.15s ease",
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Order # */}
        {visibleColumns.transaction_number && (
          <td
            className="align-middle py-4 px-4"
            style={{ minWidth: "160px", width: "160px" }}
          >
            <div className="d-flex align-items-center">
              <button
                type="button"
                className="btn btn-xs btn-icon btn-clean mr-2 text-muted flex-shrink-0"
                title={isExpanded ? "Collapse Details" : "Expand Details"}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
              >
                <i
                  className={`fas fa-chevron-${
                    isExpanded ? "down" : "right"
                  } font-size-xs`}
                />
              </button>
              {webview ? (
                <span className="font-weight-bolder text-dark font-size-sm">
                  #{transaction_number}
                </span>
              ) : (
                <Link
                  to={{
                    pathname: `/orders/${_id}`,
                    state: { from: pathname },
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="font-weight-bolder text-primary text-hover-primary font-size-sm"
                  title="View Actual Order"
                >
                  #{transaction_number}
                </Link>
              )}
            </div>
          </td>
        )}

        {/* Order Date */}
        {visibleColumns.order_date && (
          <td
            className="align-middle py-4 px-4"
            style={{ minWidth: "160px", width: "160px" }}
          >
            <div className="d-flex flex-column">
              <span className="font-weight-bold text-dark font-size-sm">
                {order_date || "-"}
              </span>
              {order_time && (
                <span className="text-muted font-size-xs d-block mt-1">
                  <i className="far fa-clock mr-1 font-size-xs text-muted" />
                  {order_time === "asap_order_time" ? "ASAP" : order_time}
                </span>
              )}
            </div>
          </td>
        )}

        {/* Store */}
        {visibleColumns.store_name && (
          <td
            className="align-middle py-4 px-4"
            style={{ minWidth: "180px", width: "180px" }}
          >
            <span className="font-weight-bold text-dark-75 font-size-sm">
              {store_name || "-"}
            </span>
          </td>
        )}

        {/* Order Type */}
        {visibleColumns.order_type && (
          <td
            className="align-middle py-4 px-4"
            style={{ minWidth: "160px", width: "160px" }}
          >
            {order_type ? (
              (() => {
                const cfg = getOrderTypeConfig(order_type);
                return (
                  <span
                    className={`badge font-weight-bold text-capitalize py-2 px-3 d-inline-flex align-items-center ${cfg.badgeClass}`}
                    style={cfg.style}
                  >
                    <i
                      className={`${cfg.icon} mr-2 font-size-sm ${cfg.iconColorClass}`}
                      style={cfg.iconStyle}
                    />
                    <span>{Humanize(order_type)}</span>
                  </span>
                );
              })()
            ) : (
              <span className="text-muted font-size-sm">-</span>
            )}
          </td>
        )}

        {/* Pre-Order */}
        {visibleColumns.pre_order && (
          <td
            className="align-middle py-4 px-4"
            style={{ minWidth: "130px", width: "130px" }}
          >
            {isPreOrder ? (
              <span
                className="badge font-weight-bolder text-uppercase py-1 px-2 font-size-xs d-inline-flex align-items-center"
                style={{
                  backgroundColor: "rgba(137, 80, 252, 0.12)",
                  color: "#8950fc",
                  border: "1px solid rgba(137, 80, 252, 0.25)",
                  borderRadius: "4px",
                }}
              >
                <i
                  className="far fa-calendar-alt mr-1 font-size-xs"
                  style={{ color: "#8950fc" }}
                />
                Pre-Order
              </span>
            ) : (
              <span className="text-muted font-size-sm">-</span>
            )}
          </td>
        )}

        {/* Delivery Partner */}
        {visibleColumns.delivery_partner && (
          <td
            className="align-middle py-4 px-4"
            style={{ minWidth: "140px", width: "140px" }}
          >
            {delivery_partner ? (
              <span className="badge badge-light-info text-capitalize font-weight-bold py-2 px-3">
                <i className="fas fa-truck mr-1 text-info" />
                {Humanize(delivery_partner)}
              </span>
            ) : (
              <span className="text-muted font-size-sm">-</span>
            )}
          </td>
        )}

        {/* Payment ID */}
        {visibleColumns.payment_details && (
          <td
            className="align-middle py-4 px-4"
            style={{ minWidth: "160px", width: "160px" }}
          >
            {payment_details ? (
              <span className="text-dark-75 font-weight-bold font-size-xs font-monospace">
                {decryptPaymentDetails(payment_details)}
              </span>
            ) : (
              <span className="text-muted font-size-sm">-</span>
            )}
          </td>
        )}

        {/* Payment Method */}
        {visibleColumns.payment_method && (
          <td
            className="align-middle py-4 px-4"
            style={{ minWidth: "170px", width: "170px" }}
          >
            <span className="font-weight-bold text-dark-75 font-size-sm">
              {Humanize(payment_method) || "-"}
            </span>
          </td>
        )}

        {/* Customer Email */}
        {visibleColumns.email && (
          <td
            className="align-middle py-4 px-4"
            style={{ minWidth: "240px", width: "240px" }}
          >
            <div className="d-flex align-items-center">
              <span
                className="text-dark-75 font-size-sm font-weight-normal text-truncate"
                title={email}
              >
                {email || "-"}
              </span>
              {returning_customer && (
                <OverlayTrigger
                  placement="right"
                  delay={{ show: 250, hide: 400 }}
                  overlay={renderTooltip}
                >
                  <span className="ml-2 flex-shrink-0 cursor-pointer">
                    <SVGIcon
                      variant="success"
                      title=" "
                      icon={"Navigation/Double-check"}
                    />
                  </span>
                </OverlayTrigger>
              )}
            </div>
          </td>
        )}

        {/* Customer Name */}
        {visibleColumns.customer_name && (
          <td
            className="align-middle py-4 px-4"
            style={{ minWidth: "180px", width: "180px" }}
          >
            <span className="font-weight-bold text-dark font-size-sm">
              {customer_name || "-"}
            </span>
          </td>
        )}

        {/* Contact # */}
        {visibleColumns.contact_number && (
          <td
            className="align-middle py-4 px-4"
            style={{ minWidth: "150px", width: "150px" }}
          >
            <span className="text-dark-75 font-size-sm font-weight-bold">
              {contact_number || "-"}
            </span>
          </td>
        )}

        {/* Delivery Address */}
        {visibleColumns.delivery_address && (
          <td
            className="align-middle py-4 px-4"
            style={{ minWidth: "300px", width: "320px", maxWidth: "360px" }}
          >
            <span
              className="d-block text-dark-75 font-size-sm text-truncate"
              style={{ maxWidth: "320px", lineHeight: "1.4" }}
              title={delivery_address || ""}
            >
              {delivery_address || "-"}
            </span>
          </td>
        )}

        {/* Delivery Charge */}
        {visibleColumns.chargedDeliveryFee && (
          <td
            className="align-middle py-4 px-4"
            style={{ minWidth: "160px", width: "160px" }}
          >
            <span className="font-weight-bold text-dark font-size-sm">
              {chargedDeliveryFee ? `${toPesoAmount(chargedDeliveryFee)}` : "-"}
            </span>
          </td>
        )}

        {/* Remarks */}
        {visibleColumns.remarks && (
          <td
            className="align-middle py-4 px-4"
            style={{ minWidth: "190px", width: "190px" }}
            onClick={(e) => e.stopPropagation()}
          >
            {remarks ? (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id={`remarks-${_id}`}>{remarks}</Tooltip>}
              >
                <span
                  className="d-inline-flex align-items-center text-truncate bg-light-warning text-dark px-3 py-2 rounded font-size-xs cursor-pointer border border-warning"
                  style={{ maxWidth: "180px" }}
                >
                  <i className="fas fa-comment-alt mr-2 text-warning font-size-xs flex-shrink-0" />
                  <span className="text-truncate">{remarks}</span>
                </span>
              </OverlayTrigger>
            ) : (
              <span className="text-muted font-size-sm">-</span>
            )}
          </td>
        )}

        {/* Status */}
        {visibleColumns.status && (
          <td
            className="align-middle py-4 px-4"
            style={{ minWidth: "160px", width: "160px" }}
          >
            <span
              className={`badge py-2 px-3 font-weight-bolder text-uppercase font-size-xs ${getStatusBadgeClass(
                status,
              )}`}
            >
              {getStatusDisplay(status, order_type, isPreOrder)}
            </span>
          </td>
        )}

        {/* Total Amount */}
        {visibleColumns.amount && (
          <td
            className="align-middle py-4 px-4"
            style={{ minWidth: "160px", width: "160px" }}
          >
            <div
              className="font-weight-bolder text-dark font-size-sm"
              style={{ overflow: "hidden", whiteSpace: "nowrap" }}
            >
              {totalAmountDisplay(order)}
            </div>
          </td>
        )}

        {/* Discounts */}
        {visibleColumns.discounts && (
          <td
            className="align-middle py-4 px-4"
            style={{ minWidth: "160px", width: "160px" }}
          >
            <div className="d-flex flex-column">
              {promo > 0 && (
                <span className="badge badge-light-warning text-warning font-size-xs font-weight-bolder py-1 px-2 mb-1 text-left">
                  Promo: -{toPesoAmount(promo)}
                </span>
              )}
              {totalItemDiscount > 0 && (
                <span className="badge badge-light-danger text-danger font-size-xs font-weight-bolder py-1 px-2 text-left">
                  Disc: -{toPesoAmount(totalItemDiscount)}
                </span>
              )}
              {promo <= 0 && totalItemDiscount <= 0 && (
                <span className="text-muted font-size-sm">-</span>
              )}
            </div>
          </td>
        )}

        {/* Activity Timeline Button */}
        {visibleColumns.activity && (
          <td
            className="align-middle text-center py-4 px-4"
            style={{ minWidth: "140px", width: "140px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="outline-info"
              size="sm"
              className="font-weight-bold font-size-xs py-1 px-3 rounded-pill"
              onClick={onSelectTimeline}
            >
              <i className="far fa-clock mr-1" />
              {timestampCount > 0 ? `${timestampCount} Steps` : "Timeline"}
            </Button>
          </td>
        )}

        {/* Items (Breakdown Preview) */}
        {visibleColumns.items && (
          <td
            className="align-middle py-4 px-4"
            style={{ minWidth: "330px", width: "350px" }}
          >
            {(() => {
              if (!order_details || order_details.length === 0) {
                return <span className="text-muted font-size-sm">-</span>;
              }

              const totalItemsCount = order_details.length;
              const totalQty = order_details.reduce(
                (sum, d) => sum + (Number(d.qty) || 0),
                0,
              );

              const firstDetail = order_details[0];
              const firstItemObj = firstDetail.item || {};
              const firstItemName = firstItemObj.name
                ? normalizeText(firstItemObj.name.replace(/['"]+/g, ""))
                : "Unknown Item";
              const firstQty = firstDetail.qty || 1;
              const firstUnitPrice =
                firstDetail.price !== undefined && firstDetail.price !== null
                  ? Number(firstDetail.price)
                  : 0;
              const firstLineTotal = firstUnitPrice * firstQty;
              const firstHasExtras = Boolean(
                firstDetail.extras &&
                  Array.isArray(firstDetail.extras) &&
                  firstDetail.extras.length > 0,
              );
              const hasMultipleItems = totalItemsCount > 1;

              if (!isItemsExpanded) {
                return (
                  <div className="d-flex align-items-center justify-content-between text-nowrap">
                    <span
                      className="font-weight-bold text-dark font-size-sm text-truncate mr-2"
                      style={{
                        maxWidth:
                          hasMultipleItems || firstHasExtras
                            ? "190px"
                            : "270px",
                      }}
                      title={`${firstQty}x ${firstItemName}${
                        hasMultipleItems
                          ? ` (+${totalItemsCount - 1} more item${
                              totalItemsCount - 1 > 1 ? "s" : ""
                            })`
                          : ""
                      }`}
                    >
                      {firstQty} &times; {firstItemName}
                    </span>
                    {hasMultipleItems ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip
                            id={`items-more-${
                              transaction_number || Math.random()
                            }`}
                          >
                            <div
                              className="text-left py-1"
                              style={{ maxWidth: "280px" }}
                            >
                              <div className="font-weight-bolder mb-1 pb-1 border-bottom border-secondary text-warning">
                                All {totalItemsCount} items ({totalQty} pcs):
                              </div>
                              {order_details.map((d, idx) => {
                                const itm = d.item || {};
                                const name = itm.name
                                  ? normalizeText(
                                      itm.name.replace(/['"]+/g, ""),
                                    )
                                  : "Item";
                                return (
                                  <div
                                    key={idx}
                                    className="font-size-xs text-nowrap py-1"
                                  >
                                    <strong>{d.qty || 1} &times;</strong> {name}
                                  </div>
                                );
                              })}
                            </div>
                          </Tooltip>
                        }
                      >
                        <span
                          className="badge badge-light-primary text-primary font-weight-bolder border border-primary font-size-xs py-1 px-2 flex-shrink-0 d-inline-flex align-items-center shadow-xs cursor-pointer"
                          style={{
                            backgroundColor: "#ebf5ff",
                            borderColor: "#3699FF",
                            letterSpacing: "0.2px",
                          }}
                        >
                          <i className="fas fa-layer-group font-size-xs mr-1 text-primary" />
                          +{totalItemsCount - 1} more
                        </span>
                      </OverlayTrigger>
                    ) : firstHasExtras ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip
                            id={`items-extras-${
                              transaction_number || Math.random()
                            }`}
                          >
                            <div className="text-left font-size-xs py-1">
                              <strong className="text-warning">
                                Extras / Options:
                              </strong>
                              {firstDetail.extras.map((ex, j) => {
                                const exName =
                                  ex.extra_id && ex.extra_id[0]?.name
                                    ? ex.extra_id[0].name
                                    : ex.extra_id?.name || "";
                                return exName ? (
                                  <div key={j} className="mt-1">
                                    + {exName}
                                  </div>
                                ) : null;
                              })}
                            </div>
                          </Tooltip>
                        }
                      >
                        <span
                          className="badge badge-light-info text-info font-weight-bolder border border-info font-size-xs py-1 px-2 flex-shrink-0 d-inline-flex align-items-center shadow-xs cursor-pointer"
                          style={{
                            backgroundColor: "#f3f6f9",
                          }}
                        >
                          <i className="fas fa-plus font-size-xs mr-1" />
                          with extras
                        </span>
                      </OverlayTrigger>
                    ) : firstUnitPrice > 0 ? (
                      <span className="text-muted font-size-xs font-weight-bold flex-shrink-0">
                        {toPesoAmount(firstLineTotal)}
                      </span>
                    ) : null}
                  </div>
                );
              }

              return (
                <div className="position-relative">
                  <div className="mb-2 pb-1 border-bottom border-light">
                    <span className="badge badge-light-primary font-weight-bolder font-size-xs py-1 px-2">
                      {totalQty} item{totalQty !== 1 ? "s" : ""} (
                      {totalItemsCount} line{totalItemsCount !== 1 ? "s" : ""})
                    </span>
                  </div>
                  <div
                    className="pr-1"
                    style={{ maxHeight: "240px", overflowY: "auto" }}
                  >
                    {order_details.map((detail, i) => {
                      const itemObj = detail.item || {};
                      const itemName = itemObj.name
                        ? normalizeText(itemObj.name.replace(/['"]+/g, ""))
                        : "Unknown Item";
                      const qty = detail.qty || 0;
                      const unitPrice =
                        detail.price !== undefined && detail.price !== null
                          ? Number(detail.price)
                          : 0;
                      const lineTotal = unitPrice * qty;

                      return (
                        <div
                          key={(itemObj._id || "") + i}
                          className="mb-2 pb-1 border-bottom border-light"
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span className="font-weight-bold text-dark font-size-sm">
                              {qty} &times; {itemName}
                            </span>
                            {unitPrice > 0 && (
                              <span className="text-muted font-size-xs font-weight-bold ml-2 text-nowrap">
                                {toPesoAmount(lineTotal)}
                              </span>
                            )}
                          </div>
                          {detail.extras &&
                            Array.isArray(detail.extras) &&
                            detail.extras.length > 0 && (
                              <div className="ml-3 font-size-xs text-muted">
                                {detail.extras.map((extra, j) => {
                                  const extraName =
                                    extra.extra_id && extra.extra_id[0]?.name
                                      ? extra.extra_id[0].name
                                      : extra.extra_id?.name || "";
                                  return extraName ? (
                                    <span
                                      key={j}
                                      className="mr-2 d-inline-block"
                                    >
                                      + {extraName}
                                    </span>
                                  ) : null;
                                })}
                              </div>
                            )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </td>
        )}
      </tr>

      {/* Expanded Row Details */}
      {isExpanded && (
        <tr className="bg-light-light">
          <td
            colSpan={visibleColumnCount}
            className="p-4"
            style={{ position: "relative" }}
          >
            <div
              className="bg-white p-5 rounded border shadow-sm"
              style={{
                position: "sticky",
                left: "20px",
                width: visibleWidth
                  ? `${Math.max(320, visibleWidth - 40)}px`
                  : "calc(100vw - 60px)",
                maxWidth: "calc(100vw - 40px)",
                boxSizing: "border-box",
              }}
            >
              <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                <div className="d-flex align-items-center">
                  <span className="btn btn-icon btn-light-primary btn-circle btn-sm mr-3">
                    <i className="fas fa-list-alt text-primary font-size-sm" />
                  </span>
                  <div>
                    <h6 className="font-weight-bolder text-dark mb-0 font-size-h6">
                      Item Breakdown for Order #{transaction_number}
                    </h6>
                    <span className="text-muted font-size-xs">
                      {order_details.length} item
                      {order_details.length !== 1 ? "s" : ""} in total
                    </span>
                  </div>
                </div>
                <div className="d-flex align-items-center flex-wrap">
                  {isPreOrder && (
                    <span
                      className="badge mr-2 font-weight-bolder text-uppercase py-2 px-3 d-inline-flex align-items-center"
                      style={{
                        backgroundColor: "rgba(137, 80, 252, 0.12)",
                        color: "#8950fc",
                        border: "1px solid rgba(137, 80, 252, 0.25)",
                      }}
                    >
                      <i
                        className="far fa-calendar-alt mr-1 font-size-xs"
                        style={{ color: "#8950fc" }}
                      />
                      Pre-Order
                    </span>
                  )}
                  {order_type &&
                    (() => {
                      const cfg = getOrderTypeConfig(order_type);
                      return (
                        <span
                          className={`badge mr-2 font-weight-bolder text-capitalize py-2 px-3 d-inline-flex align-items-center ${cfg.badgeClass}`}
                          style={cfg.style}
                        >
                          <i
                            className={`${cfg.icon} mr-1 font-size-xs ${cfg.iconColorClass}`}
                            style={cfg.iconStyle}
                          />
                          {Humanize(order_type)}
                        </span>
                      );
                    })()}
                  {store_name && (
                    <span className="badge badge-light-primary mr-2 font-weight-bolder py-2 px-3">
                      Store: {store_name}
                    </span>
                  )}
                  {delivery_partner && (
                    <span className="badge badge-light-info mr-2 font-weight-bolder text-capitalize py-2 px-3">
                      Partner: {Humanize(delivery_partner)}
                    </span>
                  )}
                  {!webview && _id && (
                    <Link
                      to={{
                        pathname: `/orders/${_id}`,
                        state: { from: pathname },
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="btn btn-primary btn-sm py-1 px-3 font-size-xs font-weight-bold rounded-pill mr-2 shadow-xs d-inline-flex align-items-center text-white"
                      title="Open actual order details page"
                    >
                      <i className="fas fa-external-link-alt mr-1 font-size-xs" />
                      View Actual Order
                    </Link>
                  )}
                  <Button
                    variant="outline-info"
                    size="sm"
                    className="py-1 px-3 font-size-xs font-weight-bold rounded-pill"
                    onClick={onSelectTimeline}
                  >
                    <i className="far fa-clock mr-1" />
                    View Timeline Steps
                  </Button>
                </div>
              </div>

              {/* Remarks in drawer */}
              {remarks && (
                <div className="alert alert-custom alert-light-warning p-3 mb-4 font-size-sm border border-warning rounded">
                  <div className="alert-icon">
                    <i className="fas fa-comment-alt text-warning" />
                  </div>
                  <div className="alert-text font-size-sm">
                    <strong>Customer Remarks:</strong> {remarks}
                  </div>
                </div>
              )}

              {/* Items Breakdown Sub-table */}
              <div className="table-responsive rounded border">
                <Table size="sm" hover className="mb-0">
                  <thead className="thead-light">
                    <tr className="font-size-xs text-uppercase text-secondary font-weight-bolder">
                      <th className="py-3 px-4">Item / Product Name</th>
                      <th className="py-3 px-3">SKU</th>
                      <th className="py-3 px-3">Extras / Options</th>
                      <th className="py-3 px-3 text-center">Qty</th>
                      <th className="py-3 px-3 text-right">Unit Price</th>
                      <th className="py-3 px-3 text-right">Line Total</th>
                      <th className="py-3 px-4 text-right">Discount</th>
                    </tr>
                  </thead>
                  <tbody className="font-size-sm">
                    {order_details.map((detail, idx) => {
                      const itemObj = detail.item || {};
                      const itemName = itemObj.name
                        ? normalizeText(itemObj.name.replace(/['"]+/g, ""))
                        : "Unknown Item";
                      const itemCode = itemObj.item_code || "-";
                      const qty = Number(detail.qty) || 0;
                      const unitPrice =
                        detail.price !== undefined && detail.price !== null
                          ? Number(detail.price)
                          : 0;
                      const regularPrice =
                        detail.regular_price !== undefined &&
                        detail.regular_price !== null
                          ? Number(detail.regular_price)
                          : unitPrice;
                      const lineTotal = unitPrice * qty;
                      const itemDiscount = Math.max(
                        0,
                        (regularPrice - unitPrice) * qty,
                      );

                      let extrasText = "-";
                      if (
                        Array.isArray(detail.extras) &&
                        detail.extras.length > 0
                      ) {
                        const extraNames = detail.extras
                          .map((ext) => {
                            if (
                              Array.isArray(ext.extra_id) &&
                              ext.extra_id[0]?.name
                            ) {
                              return normalizeText(ext.extra_id[0].name);
                            }
                            if (ext.extra_id?.name) {
                              return normalizeText(ext.extra_id.name);
                            }
                            return null;
                          })
                          .filter(Boolean);
                        if (extraNames.length > 0) {
                          extrasText = extraNames.join(", ");
                        }
                      }

                      return (
                        <tr key={idx} className="border-bottom">
                          <td className="font-weight-bold text-dark py-3 px-4">
                            {itemName}
                          </td>
                          <td className="text-muted py-3 px-3">{itemCode}</td>
                          <td className="text-muted py-3 px-3">{extrasText}</td>
                          <td className="text-center font-weight-bold py-3 px-3">
                            {qty}
                          </td>
                          <td className="text-right py-3 px-3">
                            <span className="font-weight-bold">
                              {toPesoAmount(unitPrice)}
                            </span>
                            {regularPrice > unitPrice && (
                              <span className="text-muted text-decoration-line-through ml-2 font-size-xs">
                                {toPesoAmount(regularPrice)}
                              </span>
                            )}
                          </td>
                          <td className="text-right font-weight-bolder text-primary py-3 px-3">
                            {toPesoAmount(lineTotal)}
                          </td>
                          <td className="text-right py-3 px-4">
                            {itemDiscount > 0 ? (
                              <span className="text-danger font-weight-bold">
                                -{toPesoAmount(itemDiscount)}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

const renderTooltip = (props) => (
  <Tooltip id="button-tooltip-returning-customer" {...props}>
    Returning Customer
  </Tooltip>
);

export default ReportsTable;
